import React from 'react';
import { Bold, Italic, Underline, Strikethrough, Link, Code, Save } from 'lucide-react';
import StatusIndicator from '@amzn/awsui-components-console/status-indicator';
import './Toolbar.scss';
interface ToolbarProps {
  isEditMode: boolean;
  hasSelection: boolean;
  onFormat: (command: string) => void;
  isSourceView?: boolean;
  hasSourceChanges?: boolean;
  onToggleSourceView?: () => void;
  onSaveSource?: () => void;
  saveStatus?: 'idle' | 'saving' | 'success' | 'error' | 'edited';
}

const Toolbar: React.FC<ToolbarProps> = ({
  isEditMode,
  hasSelection,
  onFormat,
  isSourceView = false,
  hasSourceChanges = false,
  onToggleSourceView,
  onSaveSource,
  saveStatus = 'idle',
}) => {
  const handleFormatClick = (command: string) => {
    onFormat(command);
  };

  const keepEditorFocused = (e: React.MouseEvent | React.PointerEvent) => {
    // Prevent button from taking focus on mouse/pointer interaction
    e.preventDefault();
    console.log('Prevent focused');
  };

  const isDisabled = !isEditMode || !hasSelection || isSourceView;

  return (
    <div className="editor-toolbar" onMouseDown={keepEditorFocused} onPointerDown={keepEditorFocused}>
      <button className="toolbar-button" onClick={() => handleFormatClick('bold')} disabled={isDisabled} title="Bold" aria-label="Bold">
        <Bold size={18} />
      </button>
      <button
        className="toolbar-button"
        onClick={() => handleFormatClick('italic')}
        disabled={isDisabled}
        title="Italic"
        aria-label="Italic"
      >
        <Italic size={18} />
      </button>
      <button
        className="toolbar-button"
        onClick={() => handleFormatClick('underline')}
        disabled={isDisabled}
        title="Underline"
        aria-label="Underline"
      >
        <Underline size={18} />
      </button>
      <button
        className="toolbar-button"
        onClick={() => handleFormatClick('strikethrough')}
        disabled={isDisabled}
        title="Strikethrough"
        aria-label="Strikethrough"
      >
        <Strikethrough size={18} />
      </button>
      <button className="toolbar-button" onClick={() => handleFormatClick('link')} disabled={isDisabled} title="Link" aria-label="Link">
        <Link size={18} />
      </button>
      <div className="toolbar-separator" />
      <button
        className={`toolbar-button ${isSourceView ? 'active' : ''}`}
        onClick={onToggleSourceView}
        disabled={!isEditMode}
        title={isSourceView ? 'View Rendered' : 'View Source'}
        aria-label={isSourceView ? 'View Rendered' : 'View Source'}
      >
        <Code size={18} />
      </button>
      {isSourceView && (
        <button
          className="toolbar-button"
          onClick={onSaveSource}
          disabled={!isEditMode || !hasSourceChanges}
          title="Save Changes"
          aria-label="Save Changes"
        >
          <Save size={18} />
        </button>
      )}
      {saveStatus !== 'idle' && (
        <div className="toolbar-status" style={{ marginLeft: 'auto', paddingRight: '8px', display: 'flex', alignItems: 'center' }}>
          {saveStatus === 'edited' && <StatusIndicator type="in-progress">Not saved</StatusIndicator>}
          {saveStatus === 'saving' && <StatusIndicator type="in-progress">Saving...</StatusIndicator>}
          {saveStatus === 'success' && <StatusIndicator>Saved</StatusIndicator>}
          {saveStatus === 'error' && <StatusIndicator type="error">Save failed</StatusIndicator>}
        </div>
      )}
    </div>
  );
};

export default Toolbar;
